import React, { useEffect, useState } from "react";
import { Bell, ClipboardList, FileText, HardHat, BarChart3, Settings, LayoutDashboard, LogOut, Menu, PhoneCall, Users, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { clearAuth, getSession } from "../../../app/auth";
import "../pages/AdminDashboard.css";
import "../pages/AdminWorkspace.css";
import "../../../styles/portalTypography.css";
import "../../../styles/brandLogo.css";
import PortalBreadcrumb from "./PortalBreadcrumb";
import logo from "../../../assets/thumbnails/logoOnly.png";

const menuItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/admin/dashboard" },
  { label: "Projects", icon: HardHat, path: "/admin/projects" },
  { label: "Clients", icon: Users, path: "/admin/clients" },
  { label: "Callback Requests", icon: PhoneCall, path: "/admin/callbacks" },
  { label: "Reports", icon: BarChart3, unavailable: true },
  { label: "Settings", icon: Settings, unavailable: true },
  { label: "Admin Accounts", icon: Users, superadminOnly: true, path: "/admin/accounts" },
];

export default function AdminLayout({ children, activeItem = "Projects", onSectionChange, breadcrumb, portal = "admin" }) {
  const navigate = useNavigate();
  const isClient = portal === "client";
  const navigation = isClient ? [
    { label: "Dashboard", icon: LayoutDashboard, path: "/client/dashboard" },
    { label: "Projects", icon: HardHat, path: "/client/projects" },
    { label: "Project Updates", icon: ClipboardList, path: "/client/updates" },
    { label: "Documents", icon: FileText, path: "/client/documents" },
  ] : menuItems.filter((item) => !item.superadminOnly || getSession()?.role === "SUPERADMIN");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const userName =
    localStorage.getItem("userName") ||
    localStorage.getItem("username") ||
    (isClient ? "Client" : "Admin");
  const userInitials = userName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((name) => name[0].toUpperCase())
    .join("");

  useEffect(() => {
    if (!sidebarOpen) return;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setSidebarOpen(false);
        document.querySelector(".admin-menu-button")?.focus();
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [sidebarOpen]);

  const handleMenuClick = (item) => {
    if (item.path) { navigate(item.path); setSidebarOpen(false); return; }
    if (onSectionChange) onSectionChange(item.label);
    else navigate("/admin/dashboard", { state: { activeItem: item.label } });
    setSidebarOpen(false);
    if (item.path) navigate(item.path);
  };

  return (
    <div className={"admin-dashboard admin-workspace portal-theme" + (isClient ? " client-workspace" : "")}>
      <aside id="admin-navigation" className={`admin-sidebar ${sidebarOpen ? "is-open" : ""}`}>
        <div className="admin-brand">
          <img src={logo} className="spirent-logo" alt="Spirent India" />
          <button
            className="admin-close-sidebar"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>
        <div className="admin-role">WORKSPACE</div>
        <nav className="admin-nav" aria-label={isClient ? "Client navigation" : "Admin navigation"}>
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <React.Fragment key={item.label}>
                {item.label === "Settings" && <div className="admin-role admin-administration">ADMINISTRATION</div>}
                <button
                  className={"admin-nav-item " + (activeItem === item.label ? "active" : "")}
                  onClick={() => handleMenuClick(item)}
                  disabled={item.unavailable}
                  title={item.unavailable ? item.label + " is not available yet" : undefined}
                  aria-current={activeItem === item.label ? "page" : undefined}
                >
                  <Icon size={18} /><span>{item.label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </nav>
        <div className="admin-sidebar-bottom">
          <div className="admin-sidebar-user">
            <div className="admin-avatar">{userInitials || "A"}</div>
            <div><strong>{userName}</strong><small>{isClient ? "Client" : String(localStorage.getItem("role") || "").toUpperCase() === "SUPERADMIN" ? "Super administrator" : "Administrator"}</small></div>
            {!isClient && <button className="admin-signout" title="Log out" aria-label="Log out" onClick={() => { clearAuth(); navigate("/", { replace: true }); }}><LogOut size={17} /></button>}
          </div>
          <button className="admin-logout" onClick={() => { if (isClient) clearAuth(); navigate(isClient ? "/" : "/home", { replace: isClient }); }}>{isClient ? <><LogOut size={16} />Log out</> : "Back to website"}</button>
        </div>
      </aside>

      {sidebarOpen && (
        <button
          className="admin-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close menu"
        />
      )}

      <main className="admin-main">
        <header className="admin-header">
          <button
            className="admin-menu-button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation" aria-expanded={sidebarOpen} aria-controls="admin-navigation"
          >
            <Menu size={21} />
          </button>
          {breadcrumb || <PortalBreadcrumb portal={portal} section={activeItem} />}
          <div className="admin-header-actions">
            <button className="admin-icon-button" aria-label="Notifications (not available yet)" title="Notifications are not available yet" disabled>
              <Bell size={19} />
              <span />
            </button>
            <div className="admin-user">
              <div className="admin-avatar">{userInitials || "A"}</div>
              <div>
                <strong>{userName}</strong>

              </div>
            </div>
          </div>
        </header>

        {children}
      </main>
    </div>
  );
}
